import React from 'react';

interface WateenLogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showText?: boolean;
  showTagline?: boolean;
  className?: string;
}

export const WateenLogo: React.FC<WateenLogoProps> = ({
  size = 'md',
  showText = true,
  showTagline = false,
  className = '',
}) => {
  const iconSizes = {
    sm: 'w-7 h-7',
    md: 'w-9 h-9',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
  };

  const textSizes = {
    sm: 'text-base font-bold',
    md: 'text-xl font-extrabold',
    lg: 'text-2xl font-extrabold tracking-wide',
    xl: 'text-3xl font-black tracking-wider',
  };

  return (
    <div className={`flex items-center gap-2.5 select-none ${className}`}>
      {/* Futuristic Pulse Wave Wateen Icon */}
      <div className={`relative ${iconSizes[size]} flex items-center justify-center shrink-0 group cursor-pointer`}>
        {/* Animated breathing glow aura */}
        <div className="absolute -inset-1 rounded-2xl bg-gradient-to-tr from-emerald-500 via-teal-400 to-indigo-600 blur-[10px] opacity-70 animate-pulse-halo" />
        
        {/* Living container with organic heartbeat bounce */}
        <div className="relative w-full h-full rounded-2xl bg-[#0a101d]/95 border border-emerald-500/50 shadow-inner flex items-center justify-center p-1.5 overflow-hidden animate-heartbeat group-hover:scale-110 transition-transform duration-300">
          {/* Subtle heartbeat matrix grid overlay */}
          <div className="absolute inset-0 bg-[radial-gradient(#10b981_1.2px,transparent_1.2px)] [background-size:6px_6px] opacity-25" />
          
          <svg viewBox="0 0 40 40" fill="none" className="w-full h-full relative z-10 drop-shadow-[0_0_8px_rgba(16,185,129,0.7)]">
            <defs>
              <linearGradient id="wateenGlow" x1="0%" y1="100%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#10b981" />
                <stop offset="50%" stopColor="#06b6d4" />
                <stop offset="100%" stopColor="#6366f1" />
              </linearGradient>
              <filter id="ecgGlowFilter" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="1.5" result="glow" />
                <feMerge>
                  <feMergeNode in="glow" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Base static faint path for continuous visibility */}
            <path
              d="M 5 22 L 12 22 L 16 11 L 21 29 L 25 15 L 28 22 L 35 22"
              stroke="rgba(16, 185, 129, 0.25)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Moving dynamic living ECG wave that travels continuously */}
            <path
              d="M 5 22 L 12 22 L 16 11 L 21 29 L 25 15 L 28 22 L 35 22"
              stroke="url(#wateenGlow)"
              strokeWidth="3.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              filter="url(#ecgGlowFilter)"
              className="animate-ecg"
            />

            {/* Pulsating focal beads along the heartbeat peaks */}
            <circle cx="21" cy="29" r="2.2" fill="#10b981" className="animate-ping opacity-60" style={{ animationDuration: '2.2s' }} />
            <circle cx="21" cy="29" r="2.2" fill="#10b981" />
            <circle cx="16" cy="11" r="2" fill="#06b6d4" />
          </svg>
        </div>
      </div>

      {showText && (
        <div className="flex flex-col leading-tight">
          <div className="flex items-center gap-1.5">
            <span className={`text-transparent bg-clip-text bg-gradient-to-l from-emerald-400 via-teal-300 to-indigo-300 font-['Cairo'] ${textSizes[size]}`}>
              وتين
            </span>
            <span className="text-[11px] font-mono tracking-widest text-emerald-400/80 uppercase font-semibold px-1.5 py-0.5 rounded bg-emerald-950/40 border border-emerald-500/30">
              WATEEN
            </span>
          </div>
          {showTagline && (
            <span className="text-[10px] text-slate-400 font-medium tracking-wide">
              Connected by Wateen
            </span>
          )}
        </div>
      )}
    </div>
  );
};
